<script lang="ts">
  // Attached-message (.eml) preview (spec §1.5 item 4). A capture-phase click on any
  // `a[href*="/api/attachment/"]` whose text or `download` name ends in `.eml` opens this
  // <dialog>, fetches `/api/attachment/:uid/:part/message` and shows the parsed message in a
  // sandboxed iframe (`sandbox=""`, CSP `default-src 'none'`). Mounted once by the Reader.
  import { api, ApiError } from '$lib/api/client';
  import { getLang, localeTag, t } from '$lib/i18n';
  import { formatPerson } from '$lib/mail/format';
  import { emlSrcdoc, emlStrings, isEmlAnchor, parseAttachmentHref } from './_local';

  let dlg = $state<HTMLDialogElement | null>(null);
  let subject = $state('');
  let meta = $state('');
  let err = $state('');
  let srcdoc = $state('');
  let dlHref = $state('');
  let seq = 0;

  const s = $derived(emlStrings(getLang()));

  function onDocClick(ev: MouseEvent): void {
    if (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return;
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const a = target.closest('a[href*="/api/attachment/"]') as HTMLAnchorElement | null;
    if (!a || !isEmlAnchor(a)) return;
    ev.preventDefault();
    ev.stopPropagation();
    void show(a.href);
  }

  $effect(() => {
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  });

  function people(list: readonly { name: string; address: string }[]): string {
    return list.map(formatPerson).filter(Boolean).join(getLang() === 'ar' ? '، ' : ', ');
  }

  function buildMeta(m: Awaited<ReturnType<typeof api.attachedMessage>>): string {
    const parts: string[] = [];
    if (m.from?.length) parts.push(`${t('from')}: ${people(m.from)}`);
    if (m.to?.length) parts.push(`${t('to')}: ${people(m.to)}`);
    if (m.cc?.length) parts.push(`${t('cc')}: ${people(m.cc)}`);
    if (m.date) {
      const d = new Date(m.date);
      if (!Number.isNaN(d.getTime())) parts.push(d.toLocaleString(localeTag()));
    }
    const names = (m.attachments ?? []).map((a) => a.filename).filter(Boolean);
    if (names.length) parts.push(`${t('attachments')}: ${names.join(getLang() === 'ar' ? '، ' : ', ')}`);
    return parts.join(' · ');
  }

  async function show(href: string): Promise<void> {
    const mySeq = ++seq;
    dlHref = href;
    subject = t('loading');
    meta = '';
    err = '';
    srcdoc = '';
    openDialog();
    const ref = parseAttachmentHref(href);
    if (!ref) {
      fail('message');
      return;
    }
    try {
      const m = await api.attachedMessage(ref.folder, ref.uid, ref.part);
      if (mySeq !== seq) return;
      subject = m.subject || t('noSubject');
      meta = buildMeta(m);
      srcdoc = emlSrcdoc(m.html, m.text);
    } catch (e) {
      if (mySeq !== seq) return;
      fail(e instanceof ApiError && e.status ? `http_${e.status}` : 'message');
    }
  }

  function fail(code: string): void {
    subject = s.failed;
    err = `${s.failedBody} (${code})`;
  }

  function openDialog(): void {
    const d = dlg;
    if (!d) return;
    if (typeof d.showModal === 'function') {
      if (!d.open) d.showModal();
    } else d.setAttribute('open', '');
  }

  function close(): void {
    const d = dlg;
    if (d && typeof d.close === 'function' && d.open) d.close();
    else d?.removeAttribute('open');
    srcdoc = '';
  }

  function onBackdrop(ev: MouseEvent): void {
    if (ev.target === dlg) close();
  }
</script>

<dialog class="tik-eml" bind:this={dlg} onclose={() => (srcdoc = '')} onclick={onBackdrop}>
  <div class="tik-eml-head">
    <h3 class="tik-eml-subj">{subject}</h3>
    <button type="button" class="tik-eml-x" aria-label={s.close} onclick={close}>✕</button>
  </div>
  <p class="tik-eml-meta">{meta}</p>
  <p class="tik-eml-err" role="alert" hidden={!err}>{err}</p>
  <iframe class="tik-eml-body" sandbox="" referrerpolicy="no-referrer" title={s.bodyTitle} {srcdoc}></iframe>
  <div class="tik-eml-foot">
    <a class="tik-eml-dl" download href={dlHref}>{t('downloadEml')}</a>
  </div>
</dialog>

<style>
  /* appended global block — Attached-message (.eml) preview */
  .tik-eml {
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-panel);
    padding: 0;
    inline-size: min(900px, calc(100vw - var(--space-8)));
    max-inline-size: none;
    background: var(--surface-card);
    color: var(--text-body);
    box-shadow: var(--shadow-dialog)
  }

  .tik-eml::backdrop {
    background: var(--scrim)
  }

  .tik-eml-head {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-5) var(--space-2)
  }

  .tik-eml-subj {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: var(--text-lg);
    font-weight: var(--weight-semibold);
    color: var(--text-strong);
    line-height: var(--leading-tight);
    overflow-wrap: anywhere
  }

  .tik-eml-x {
    flex: none;
    inline-size: var(--control-lg);
    block-size: var(--control-lg);
    border: none;
    border-radius: var(--radius-control);
    background: none;
    color: var(--text-muted);
    font: inherit;
    font-size: var(--text-lg);
    cursor: pointer
  }

  .tik-eml-x:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .tik-eml-meta {
    margin: 0;
    padding: 0 var(--space-5) var(--space-3);
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: var(--leading-body);
    overflow-wrap: anywhere
  }

  .tik-eml-err {
    margin: 0;
    padding: 0 var(--space-5) var(--space-3);
    color: var(--danger-500);
    font-size: var(--text-sm);
    line-height: var(--leading-body)
  }

  .tik-eml-body {
    inline-size: 100%;
    block-size: min(60dvh, 560px);
    border: 0;
    border-block: 1px solid var(--border-subtle);
    background: #fff;
    display: block
  }

  .tik-eml-foot {
    display: flex;
    justify-content: flex-end;
    padding: var(--space-3) var(--space-5)
  }

  .tik-eml-dl {
    display: inline-flex;
    align-items: center;
    min-block-size: var(--control-lg);
    padding-inline: var(--space-5);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    color: var(--text-body);
    font-size: var(--text-sm);
    text-decoration: none
  }

  .tik-eml-dl:hover {
    background: var(--surface-subtle)
  }
</style>
